import {
	Component,
	Input,
	OnInit,
	ViewChild,
	ChangeDetectionStrategy,
} from "@angular/core"
import {
	FinResource,
	Prescription,
	PrescriptionLine,
} from "@app/definitions/types"
import * as moment from "moment"
import { Modalable } from "@app/Modalable"
import { WysiwygComponent } from "@app/utils/wysiwyg/wysiwyg.component"
import { getChecked } from "@app/helpers"
import { COMMON_IMPORTS } from "@app/common-imports"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { DatePickerComponent } from "@app/utils/date-picker/date-picker.component"
import {
	BtnExtComponent,
	CancelBtnComponent,
	Check,
} from "@app/utils/utils.component"
import { PreviewFinresourceComponent } from "@app/preview-finresource/preview-finresource.component"
import { GetHeadtureRowComponent } from "@app/get-headture-row.component"

const nums = {
	f: ["אחת", "שתי", "שלוש", "ארבע", "חמש", "שש", "שבע", "שמונה", "תשע", "עשר"],
	m: [
		"אחד",
		"שני",
		"שלושה",
		"ארבעה",
		"חמישה",
		"שישה",
		"שבעה",
		"שמונה",
		"תשעה",
		"עשרה",
	],
	mult: [
		"",
		"",
		"עשרים",
		"שלושים",
		"ארבעים",
		"חמישים",
		"שישים",
		"שבעים",
		"שמונים",
		"תשעים",
	],
}

const spaceJoin = (arr: any[]) => arr.join(" ")

const andPrefix = (s: string) => "ו" + s
const multPostfix = (s: string) => s + "ם"

const getHundWord = (n: number) => {
	const hund = Math.floor(n / 100)
	if (hund < 3) {
		return hund === 1 ? "מאה" : "מאתיים"
	}
	return `${nums.f[hund - 1]} מאות`
}
const getUnderHundWord = (n: number, isMale: boolean) => {
	const underHundWord = nums[isMale ? "m" : "f"][n - 1]
	return n == 2 ? multPostfix(underHundWord) : underHundWord
}

const numToHebPillsHund = (n: number, isMale: boolean) => {
	const hundWord = getHundWord(n)
	const underHundNum = n % 100
	if (!underHundNum) {
		return hundWord
	}
	if (underHundNum <= 2) {
		return `${hundWord} ${andPrefix(getUnderHundWord(underHundNum, isMale))}`
	}
	if (underHundNum <= 20 || underHundNum % 10 == 0) {
		return `${hundWord} ${andPrefix(numToHebPillsUnder100(underHundNum, isMale))}`
	}
	return `${hundWord} ${numToHebPillsUnder100(underHundNum, isMale)}`
}

const numToHebPillsUnder100 = (n: number, isMale: boolean) => {
	const pos = (n - 1) % 10
	let word = nums[isMale ? "m" : "f"][pos]
	if (n <= 10) {
		return word
	}
	if (pos === 1) {
		word = multPostfix(word)
	}
	if (n < 20) {
		return `${word} ${nums[isMale ? "f" : "m"][9]}`
	}
	const tenWord = nums.mult[Math.floor(n / 10)]
	if (!(n % 10)) {
		return tenWord
	}
	return `${tenWord} ${andPrefix(word)}`
}

const numToHebPills = (num: number, isMale: boolean) => {
	const n = Number(num)
	switch (n) {
		case 0.25:
			return "רבע"
		case 0.4:
			return "40 מאיות"
		case 0.5:
			return "חצי"
		case 0.75:
			return "75 מאיות"
		case 3.75:
			return "שלושה ושבעים וחמש מאיות"
	}
	return n >= 100
		? numToHebPillsHund(n, isMale)
		: numToHebPillsUnder100(n, isMale)
}
const maleDosageForms = [
	"COATED TABLETS",
	"FILM COATED TABLETS",
	"TABLETS",
	"TABLETS CONTROLLED RELEASE",
	"TABLETS EXTENDED RELEASE",
	"TABLETS PROLONGED RELEASE",
]
const numbersToWordsConverter = (amount: number, dosage_form: string) => {
	// TODO array of numbers 1-50 hebrew f hebrew m english
	return numToHebPills(amount, maleDosageForms.includes(dosage_form))
}

@Component({
	selector: "app-add-prescription",
	imports: [
		COMMON_IMPORTS,
		GenInputComponent,
		LangValPipe,
		DatePickerComponent,
		Check,
		WysiwygComponent,
		CancelBtnComponent,
		BtnExtComponent,
		PreviewFinresourceComponent,
		GetHeadtureRowComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./add-prescription.component.html",
})
export class AddPrescriptionComponent extends Modalable {
	modalProps: any = null
	isGetHeadtureOpen: boolean = false
	@ViewChild("permanentRemarksWysiwyg")
	permanentRemarksWysiwyg!: WysiwygComponent

	@Input() patient_id: number = 0
	prescriptionObj: Prescription = new Prescription()
	isFormSubmittable: boolean = false //can submit (controls disabled in the submit button)
	medicpatients: any[] = []
	chooseAllChecked: boolean = false
	errors: any = {} //will hold errors for the fields in the form
	validationFields: any = {
		patient_name: { not_empty: null },
		patient_id_number: { not_empty: null },
	}
	isInPreview: boolean = false
	isDrug: boolean = false

	async ngOnInit() {
		this.prescriptionObj.prescriptionLines = []
		this.prescriptionObj.date = this.store.getToday()
		this.prescriptionObj.patient_id = this.patient_id
		//this.prescriptionObj.permanent_remarks = "" //todo - not sure if needed

		const obj = this.store.searchSiteDataItem(this.patient_id, ["patients"])
		this.prescriptionObj.patient_name = obj.name || ""
		this.prescriptionObj.patient_id_number = obj.id_number || ""
		this.prescriptionObj.address = obj.address || ""
		this.prescriptionObj.age = obj.age || 0

		this.medicpatients = (
			await this.apiService.get_gen_items("medicpatients2", {
				patient_id: this.patient_id,
			})
		).medicpatients2.map((row) => ({
			...row,
			medic_amount: parseFloat(row.medic_amount),
			errors: {},
		}))
	}
	handleCheck(row: any) {
		row.checked = !row.checked
		this.updateIsDrug()
		this.validateEntireForm()
	}
	getCheckedIsDrug() {
		return getChecked(this.medicpatients).find(
			(row) => row?.medic?.is_drug === "yes"
		)
	}
	updateIsDrug() {
		this.isDrug = !!this.getCheckedIsDrug()
		if (this.isDrug) {
			this.createPermanetRemarks()
		}
	}
	handleAmountChange() {
		this.updateIsDrug()
		this.validateEntireForm()
	}

	validateEntireForm() {
		this.errors = {}
		this.medicpatients.forEach((row) => (row.errors = {}))
		const checked = getChecked(this.medicpatients)

		checked.forEach((row) => {
			this.validator.validateField(row, row.errors, "total_amount", {
				is_positive: true,
			})
		})

		const isDrugVali = this.isDrug && { address: { not_empty: true } }
		this.validator.validateEntireForm(this.prescriptionObj, this.errors, {
			...this.validationFields,
			...isDrugVali,
		})
		this.isFormSubmittable =
			this.validator.hasNoErrors(this.errors) &&
			!!checked.length &&
			!checked.some(({ errors }) => !this.validator.hasNoErrors(errors))
	}

	chooseAll(isChecked: boolean) {
		this.medicpatients.forEach((row) => (row.checked = isChecked))
		this.updateIsDrug()
		this.validateEntireForm()
	}

	createPermanetRemarks() {
		const row = this.getCheckedIsDrug()
		const times =
			row.frequency == 1
				? this.lang.getVal("time_1")
				: `${numToHebPills(row.frequency, false)} ${this.lang.getVal("times")}`

		const amount = numbersToWordsConverter(row.amount, row.dosage_form)
		const total_amount = numbersToWordsConverter(
			row.total_amount,
			row.dosage_form
		)
		const diagnosed_with =
			row?.medic?.is_adhd === "yes"
				? this.lang.getVal("adhd_note")
				: this.lang.getVal("diagnosed_with")

		this.prescriptionObj.permanent_remarks = `
      <div style="direction:rtl;text-align:right;">
        <p>${this.lang.getVal("patient_in_my_clinic")}</p>
        <p>${diagnosed_with}</p>
        <p>
          ${this.lang.getVal("treated_with") + row.medic.heb_name}
          ${numToHebPills(row.medic.amount, true)}
          ${this.lang.getVal(row.medic.unit)}
        </p>
      ${
				row.amount == 1
					? `
            ${this.lang.getVal(`${row.dosage_form}1`)} ${amount},
            ${times} ${this.lang.getVal(`${row.frequency_unit}_on`)},
            ${this.lang.getVal("total")} ${total_amount} ${this.lang.getVal(row.dosage_form)}
          `
					: `
            ${amount} ${this.lang.getVal(row.dosage_form)},
            ${times} ${this.lang.getVal(`${row.frequency_unit}_on`)},
            ${this.lang.getVal("total")} ${total_amount} ${this.lang.getVal(row.dosage_form)}
          `
			}
      </div>
    `
		if (this.permanentRemarksWysiwyg) {
			this.permanentRemarksWysiwyg.replaceText(
				this.prescriptionObj.permanent_remarks
			)
		}
		// TODO leave this commented -- const rows = this.patient.prescriptions.filter(row => row.permanent_remarks).toSorted((a, b) => b.id - a.id);
		// if (rows.length) {
		//   initPermanentRemarks = rows[0].permanent_remarks
		// }
	}

	async handleGetHeadtureRow(headtureRow: any) {
		this.isInPreview = true

		const res: any = await this.apiService.post("get_next_projected_id", {
			tableName: "prescriptions",
		})
		const id = res?.nextId || 1

		this.isInPreview = false
		this.modalProps = {
			finresourceObj: {
				...this.prescriptionObj,
				id,
				prescriptionLines: getChecked(this.medicpatients),
				date_display: moment.utc(this.prescriptionObj.date).format("D-M-Y"),
				...(!this.isDrug && { permanent_remarks: "" }),
			},
			headtureRow: {
				...headtureRow,
				...(this.permsService?.owner_has_users && {
					signature_text: this.store.cc().prescription_signature,
					signature_logo: "",
					prescription_img: this.store.cc().prescription_img,
				}),
			},
		}
	}
	closeIfValue(ret: any = null) {
		ret && this.onClose.emit(ret)
	}
}
